/**
 * ContactForm.jsx — Form for creating/editing contacts (MUI)
 */

import { useState } from 'react';
import { Box, TextField, Button, MenuItem, Select, InputLabel, FormControl, Grid, Stack, Alert, Checkbox, FormControlLabel, Paper, Typography } from '@mui/material';
import { useCreateContact, useUpdateContact, useContacts } from '../../hooks/useContacts';
import { api } from '../../lib/api';
import { agentPhotoUrl } from '../../lib/userColors';
import { useToast } from '../layout/Toast';
import { useFocus } from '../../hooks/useFocus';
import AddressAutocomplete from '../shared/AddressAutocomplete';

export default function ContactForm({ initialContact, onSaved, onCancel }) {
  const { toast } = useToast();
  const { mutate: createContact, isPending: isCreating } = useCreateContact();
  const { mutate: updateContact, isPending: isUpdating } = useUpdateContact();
  const { data: allContacts = [] } = useContacts();
  const { isHidden } = useFocus();

  const isEdit = !!initialContact?.id;
  const isPending = isCreating || isUpdating;
  const [errors, setErrors] = useState({});
  const [dupWarning, setDupWarning] = useState(null);
  const [typeWarning, setTypeWarning] = useState(null);

  // Focus hides these unused fields, but never one the contact already has a value in
  const hideUnused = isHidden('contactForm.unusedFields');
  const savedData = initialContact?.data || {};
  const showSpeciality = !hideUnused || !!savedData.speciality;
  const showTags = !hideUnused || savedData.tags?.length > 0;
  const showFollowUp = !hideUnused || !!savedData.followUpDate || !!savedData.followUpNote;
  const showCoreCrew = !hideUnused || !!savedData.isCoreCrew;

  const [formData, setFormData] = useState({
    name: initialContact?.name || '', type: initialContact?.type || 'client',
    company: initialContact?.company || '', email: initialContact?.email || '', phone: initialContact?.phone || '',
    data: {
      role: initialContact?.data?.role || '', location: initialContact?.data?.location || '',
      web: initialContact?.data?.web || '', social: initialContact?.data?.social || '',
      speciality: initialContact?.data?.speciality || '', primaryRole: initialContact?.data?.primaryRole || '',
      tags: initialContact?.data?.tags || [], notes: initialContact?.data?.notes || '',
      followUpDate: initialContact?.data?.followUpDate || '', followUpNote: initialContact?.data?.followUpNote || '',
      interactions: initialContact?.data?.interactions || [], personColor: initialContact?.data?.personColor || '',
      inRoster: initialContact?.data?.inRoster || false, payType: initialContact?.data?.payType || 'percentage',
      defaultRate: initialContact?.data?.defaultRate || '', isCoreCrew: initialContact?.data?.isCoreCrew || false,
    },
  });

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const handleDataChange = (field, value) => setFormData((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }));
  const handleTagsChange = (value) => handleDataChange('tags', value.split(',').map((t) => t.trim()).filter((t) => t.length > 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email format';
    setErrors(errs);
    if (Object.keys(errs).length > 0) { toast(Object.values(errs)[0], 'error'); return; }

    if (!isEdit) {
      const nameLower = formData.name.trim().toLowerCase();
      const dups = allContacts.filter((c) => c.name?.toLowerCase() === nameLower || (formData.email && c.email?.toLowerCase() === formData.email.toLowerCase()));
      if (dups.length > 0 && !dupWarning) { setDupWarning(`A contact named "${dups[0].name}" already exists. Save again to confirm.`); toast('Possible duplicate found', 'error'); return; }
    }
    setDupWarning(null);

    // Retyping a contact changes which pickers it appears in — a client moved
    // to crew vanishes from the quote builder's client list, and vice versa.
    // Warn once, with real reference counts, before letting it through.
    // Counts come from narrow per-contact queries rather than the full jobs and
    // quotes tables, which this form has no other reason to load.
    if (isEdit && initialContact?.type && formData.type !== initialContact.type && !typeWarning) {
      const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
      let parts;
      try {
        const [jobRefs, quoteRefs] = await Promise.all([
          api.get(`/jobs?contact_id=${initialContact.id}`),
          api.get(`/quotes?contact_id=${initialContact.id}`),
        ]);
        const openQuotes = quoteRefs.filter((q) => q.status !== 'superseded' && q.status !== 'rejected');
        parts = [];
        if (jobRefs.length) parts.push(`${jobRefs.length} job${jobRefs.length > 1 ? 's' : ''}`);
        if (openQuotes.length) parts.push(`${openQuotes.length} open quote${openQuotes.length > 1 ? 's' : ''}`);
      } catch {
        // Counts unknown (offline, or row-scoped away from this user) — confirm
        // anyway rather than let the retype through on a silent zero.
        parts = null;
      }
      if (parts === null || parts.length) {
        setTypeWarning(
          parts === null
            ? `Couldn't check what references ${initialContact.name}. Changing type from ${cap(initialContact.type)} to ${cap(formData.type)} keeps existing links intact, but changes which lists they appear in. Save again to confirm.`
            : `${initialContact.name} is referenced by ${parts.join(' and ')}. Changing type from ${cap(initialContact.type)} to ${cap(formData.type)} keeps those links intact, but changes which lists they appear in. Save again to confirm.`
        );
        toast('Contact is in use — confirm the type change', 'error');
        return;
      }
    }
    setTypeWarning(null);

    const callbacks = {
      onSuccess: (data) => { toast(`${data.name} ${isEdit ? 'updated' : 'created'}`, 'success'); onSaved?.(data); },
      onError: (err) => toast(`Failed: ${err.message}`, 'error'),
    };

    // Merge over existing data — the backend replaces the jsonb column wholesale,
    // so non-form keys (avatar_url, outreach_status, segment, ai_research, ...) must survive
    const payload = { ...formData, data: { ...(initialContact?.data || {}), ...formData.data } };
    // Clear incomplete flag when email or phone is provided
    if (payload.data.incomplete && (payload.email?.trim() || payload.phone?.trim())) {
      delete payload.data.incomplete;
    }
    // Prospect converted via type change: keep the RE-agent headshot (mirrors useConvertToClient)
    if (initialContact?.type === 'prospect' && payload.type !== 'prospect') {
      if (!payload.data.avatar_url) payload.data.avatar_url = agentPhotoUrl(initialContact.name || payload.name);
      if (payload.type === 'client') payload.data.outreach_status = 'client';
    }

    if (isEdit) updateContact({ id: initialContact.id, ...payload }, callbacks);
    else createContact(payload, callbacks);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {dupWarning && <Alert severity="error" sx={{ mb: 2 }}>{dupWarning}</Alert>}
      {typeWarning && <Alert severity="warning" sx={{ mb: 2 }}>{typeWarning}</Alert>}

      <Grid container spacing={2}>
        <Grid size={12}><TextField fullWidth size="small" label="Name *" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} error={!!errors.name} helperText={errors.name} /></Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small"><InputLabel>Type</InputLabel>
            <Select value={formData.type} onChange={(e) => handleChange('type', e.target.value)} label="Type">
              <MenuItem value="client">Client</MenuItem><MenuItem value="crew">Crew</MenuItem><MenuItem value="vendor">Vendor</MenuItem><MenuItem value="prospect">Prospect</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Company" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} /></Grid>

        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} /></Grid>
        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Phone" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} /></Grid>

        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Role" value={formData.data.role} onChange={(e) => handleDataChange('role', e.target.value)} /></Grid>
        <Grid size={{ xs: 12, md: 6 }}><AddressAutocomplete variant="mui" label="Location" value={formData.data.location} onChange={v => handleDataChange('location', v)} /></Grid>

        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Website" type="url" value={formData.data.web} onChange={(e) => handleDataChange('web', e.target.value)} /></Grid>
        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Social" value={formData.data.social} onChange={(e) => handleDataChange('social', e.target.value)} /></Grid>

        {showSpeciality && <Grid data-focus="contactForm.unusedFields" size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Speciality" value={formData.data.speciality} onChange={(e) => handleDataChange('speciality', e.target.value)} /></Grid>}
        <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Primary Role" value={formData.data.primaryRole} onChange={(e) => handleDataChange('primaryRole', e.target.value)} /></Grid>

        {/* Crew settings */}
        {formData.type === 'crew' && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Crew Settings</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small"><InputLabel>Pay Type</InputLabel>
                    <Select value={formData.data.payType || 'percentage'} onChange={(e) => handleDataChange('payType', e.target.value)} label="Pay Type">
                      <MenuItem value="percentage">Percentage</MenuItem><MenuItem value="hourly">Hourly</MenuItem><MenuItem value="flat">Flat Rate</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Default Rate" type="number" value={formData.data.defaultRate} onChange={(e) => handleDataChange('defaultRate', e.target.value === '' ? '' : parseFloat(e.target.value))} slotProps={{ input: { inputProps: { step: '0.01' } } }} /></Grid>
                <Grid size={12}>
                  <FormControlLabel control={<Checkbox size="small" checked={formData.data.inRoster || false} onChange={(e) => handleDataChange('inRoster', e.target.checked)} />} label={<Typography variant="body2">Add to Active Roster</Typography>} />
                  {showCoreCrew && <FormControlLabel data-focus="contactForm.unusedFields" control={<Checkbox size="small" checked={formData.data.isCoreCrew || false} onChange={(e) => handleDataChange('isCoreCrew', e.target.checked)} />} label={<Typography variant="body2">Core Crew Member</Typography>} />}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {showTags && <Grid data-focus="contactForm.unusedFields" size={12}><TextField fullWidth size="small" label="Tags (comma-separated)" value={formData.data.tags.join(', ')} onChange={(e) => handleTagsChange(e.target.value)} /></Grid>}
        <Grid size={12}><TextField fullWidth size="small" label="Notes" value={formData.data.notes} onChange={(e) => handleDataChange('notes', e.target.value)} multiline rows={3} /></Grid>
        {showFollowUp && <Grid data-focus="contactForm.unusedFields" size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Follow-up Date" type="date" value={formData.data.followUpDate} onChange={(e) => handleDataChange('followUpDate', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} /></Grid>}
        {showFollowUp && <Grid data-focus="contactForm.unusedFields" size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Follow-up Note" value={formData.data.followUpNote} onChange={(e) => handleDataChange('followUpNote', e.target.value)} /></Grid>}
      </Grid>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        <Button variant="contained" type="submit" disabled={isPending}>{isPending ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
      </Stack>
    </Box>
  );
}
